import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, Link } from 'react-router-dom'
import { 
  ArrowDownTrayIcon, 
  ArrowLeftIcon, 
  PrinterIcon,
  DocumentDuplicateIcon,
  CubeIcon,
  MapPinIcon,
  ShieldCheckIcon,
  TruckIcon,
  QrCodeIcon
} from '@heroicons/react/24/outline'
import toast, { Toaster } from 'react-hot-toast'
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'

type TipoCodigo = 'EAN' | 'UPC' | 'Code128' | 'QR' | 'Interno'
type EstadoProducto = 'Disponible' | 'Cuarentena' | 'Dañado' | 'Reservado' | 'Vencido'

type PayloadEtiqueta = {
  id?: string
  sku?: string
  codigoBarras?: string
  tipoCodigo?: TipoCodigo
  numeroSerie?: string
  nombre?: string
  marca?: string
  modelo?: string
  estado?: EstadoProducto
  cantidad?: string
  peso?: number
  volumen?: number
  largo?: number
  ancho?: number
  alto?: number
  lote?: string
  proveedor?: string
  ubicacion?: string
}

const base64UrlToBytes = (input: string) => {
  const normalized = input.replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=')
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

const bytesToBase64Url = (bytes: Uint8Array) => {
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

const encodePayload = (payload: PayloadEtiqueta) => {
  const json = JSON.stringify(payload)
  const bytes = new TextEncoder().encode(json)
  return bytesToBase64Url(bytes)
}

const decodePayload = (d: string): PayloadEtiqueta | null => {
  try {
    const bytes = base64UrlToBytes(d)
    const json = new TextDecoder().decode(bytes)
    const parsed = JSON.parse(json)
    if (!parsed || typeof parsed !== 'object') return null
    return parsed as PayloadEtiqueta
  } catch {
    return null
  }
}

const esEan13Valido = (code: string) => {
  const s = String(code || '').trim()
  if (!/^\d{13}$/.test(s)) return false
  const d12 = s.slice(0, 12)
  const check = Number(s[12])
  let suma = 0
  for (let i = 0; i < d12.length; i++) {
    const n = Number(d12[i])
    const pos = i + 1
    suma += pos % 2 === 0 ? n * 3 : n
  }
  const esperado = (10 - (suma % 10)) % 10
  return check === esperado
}

const esUpcAValido = (code: string) => {
  const s = String(code || '').trim()
  if (!/^\d{12}$/.test(s)) return false
  const d11 = s.slice(0, 11)
  const check = Number(s[11])
  let suma = 0
  for (let i = 0; i < d11.length; i++) {
    const n = Number(d11[i])
    const pos = i + 1
    suma += pos % 2 === 1 ? n * 3 : n
  }
  const esperado = (10 - (suma % 10)) % 10
  return check === esperado
}

const obtenerBarcodeUrls = (codigoBarras: string, tipoCodigo: TipoCodigo | undefined) => {
  const text = String(codigoBarras || '').trim()
  if (!text) return null

  const tipoSolicitado = (tipoCodigo || 'Interno') as TipoCodigo
  const eanValido = tipoSolicitado === 'EAN' && esEan13Valido(text)
  const upcValido = tipoSolicitado === 'UPC' && esUpcAValido(text)
  const tipo: TipoCodigo =
    (tipoSolicitado === 'EAN' && !eanValido) || (tipoSolicitado === 'UPC' && !upcValido) ? 'Code128' : tipoSolicitado

  const tecCode = tipo === 'EAN' ? 'EAN13' : tipo === 'UPC' ? 'UPCA' : 'Code128'
  const primary = `https://barcode.tec-it.com/barcode.ashx?data=${encodeURIComponent(text)}&code=${tecCode}&translate-esc=false`

  const bcid = tipo === 'EAN' ? 'ean13' : tipo === 'UPC' ? 'upca' : 'code128'
  const fallback = `https://bwipjs-api.metafloor.com/?bcid=${bcid}&text=${encodeURIComponent(text)}&scale=3&includetext`

  const detalle = tipo !== tipoSolicitado ? `${tipoSolicitado} inválido` : null
  return { primary, fallback, tipo, detalle }
}

const parsearUbicacionLogistica = (ubicacionStr: string) => {
  const raw = String(ubicacionStr || '').trim()
  if (!raw) {
    return {
      almacen: 'ALM-01',
      zona: 'SEC',
      pasillo: '01',
      rack: '01',
      nivel: '01',
      posicion: '01',
      formateado: 'SEC · P01 · R01 · N01 · H01'
    }
  }

  const partes = raw.split('-')
  let almacen = 'ALM-01'
  let zona = 'SEC'
  let pasillo = '01'
  let rack = '01'
  let nivel = '01'
  let posicion = '01'

  partes.forEach(p => {
    const clean = p.trim().toUpperCase()
    if (clean.startsWith('ALM')) almacen = clean
    else if (['SEC', 'CONG', 'REFR', 'DES', 'REC', 'GEN'].includes(clean)) zona = clean
    else if (clean.startsWith('P') && clean.length <= 4) pasillo = clean.replace('P', '').padStart(2, '0')
    else if (clean.startsWith('R') && clean.length <= 4) rack = clean.replace('R', '').padStart(2, '0')
    else if (clean.startsWith('N') && clean.length <= 4) nivel = clean.replace('N', '').padStart(2, '0')
    else if ((clean.startsWith('S') || clean.startsWith('H') || clean.startsWith('POS')) && clean.length <= 5) {
      posicion = clean.replace(/[SHPOS]/g, '').padStart(2, '0')
    }
  })

  if (partes.length >= 4 && !raw.includes('P') && !raw.includes('R')) {
    zona = partes[0] || 'SEC'
    pasillo = (partes[1] || '01').padStart(2, '0')
    rack = (partes[2] || '01').padStart(2, '0')
    nivel = (partes[3] || '01').padStart(2, '0')
    posicion = (partes[4] || '01').padStart(2, '0')
  }

  return {
    almacen,
    zona,
    pasillo,
    rack,
    nivel,
    posicion,
    formateado: `${zona} · P${pasillo} · R${rack} · N${nivel} · H${posicion}`
  }
}

const waitForImages = async (root: HTMLElement) => {
  const imgs = Array.from(root.querySelectorAll('img'))
  if (!imgs.length) return
  await Promise.all(
    imgs.map(img => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve()
      return new Promise<void>(resolve => {
        const done = () => resolve()
        img.addEventListener('load', done, { once: true })
        img.addEventListener('error', done, { once: true })
      })
    })
  )
}

const normalizarNumero = (value: unknown) => {
  const n = Number(value)
  if (!Number.isFinite(n) || n < 0) return 0
  return n
}

const blobToDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = () => reject(new Error('No se pudo leer la imagen'))
    reader.readAsDataURL(blob)
  })

const fetchImageAsDataUrl = async (url: string) => {
  const res = await fetch(url, { mode: 'cors', cache: 'no-store' })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const blob = await res.blob()
  return await blobToDataUrl(blob)
}

const safeFilePart = (value: string) => value.replace(/[^\w.-]+/g, '_').slice(0, 80)

const PaginaEtiquetaProductoQR: React.FC = () => {
  const location = useLocation()
  const contentRef = useRef<HTMLDivElement>(null)
  const [generando, setGenerando] = useState(false)
  const [autoIntentado, setAutoIntentado] = useState(false)
  const [barcodeDataUrl, setBarcodeDataUrl] = useState<string>('')
  const barcodePromiseRef = useRef<Promise<void> | null>(null)

  const d = useMemo(() => new URLSearchParams(location.search).get('d') || '', [location.search])
  const payload = useMemo(() => (d ? decodePayload(d) : null), [d])

  const urlSelf = useMemo(() => {
    const url = new URL('/qr/almacen/etiqueta', window.location.origin)
    const actual = new URLSearchParams(location.search).get('d')
    if (actual) url.searchParams.set('d', actual)
    return url.toString()
  }, [location.search])

  const qrImg = useMemo(() => {
    const data = encodeURIComponent(urlSelf)
    return `https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${data}`
  }, [urlSelf])

  const barcode = useMemo(() => {
    if (!payload) return null
    return obtenerBarcodeUrls(String(payload.codigoBarras || '').trim(), payload.tipoCodigo)
  }, [payload])

  useEffect(() => {
    const text = String(payload?.codigoBarras || '').trim()
    if (!payload || !text) {
      setBarcodeDataUrl('')
      barcodePromiseRef.current = null
      return
    }

    const tipoSolicitado = (payload.tipoCodigo || 'Interno') as TipoCodigo
    const eanValido = tipoSolicitado === 'EAN' && esEan13Valido(text)
    const upcValido = tipoSolicitado === 'UPC' && esUpcAValido(text)
    const tipo: TipoCodigo =
      (tipoSolicitado === 'EAN' && !eanValido) || (tipoSolicitado === 'UPC' && !upcValido) ? 'Code128' : tipoSolicitado

    const bcid = tipo === 'EAN' ? 'ean13' : tipo === 'UPC' ? 'upca' : 'code128'
    const bwip = `https://bwipjs-api.metafloor.com/?bcid=${bcid}&text=${encodeURIComponent(text)}&scale=3&includetext&paddingheight=3`
    const tecCode = tipo === 'EAN' ? 'EAN13' : tipo === 'UPC' ? 'UPCA' : 'Code128'
    const tec = `https://barcode.tec-it.com/barcode.ashx?data=${encodeURIComponent(text)}&code=${tecCode}&translate-esc=false`

    const p = (async () => {
      try {
        const dataUrl = await fetchImageAsDataUrl(bwip)
        setBarcodeDataUrl(dataUrl)
        return
      } catch {}
      try {
        const dataUrl = await fetchImageAsDataUrl(tec)
        setBarcodeDataUrl(dataUrl)
      } catch {
        setBarcodeDataUrl('')
      }
    })()

    barcodePromiseRef.current = p
  }, [payload?.codigoBarras, payload?.tipoCodigo, payload])

  const handleDownloadPDF = async () => {
    if (!contentRef.current) return
    try {
      setGenerando(true)
      const needsBarcode = Boolean(String(payload?.codigoBarras || '').trim())
      if (needsBarcode && !barcodeDataUrl) {
        await Promise.race([
          barcodePromiseRef.current ?? Promise.resolve(),
          new Promise<void>(r => window.setTimeout(() => r(), 900)),
        ])
      }
      await waitForImages(contentRef.current)
      const canvas = await html2canvas(contentRef.current, {
        scale: 3,
        logging: false,
        useCORS: true,
        backgroundColor: '#ffffff',
        windowWidth: 1200
      } as any)

      const imgData = canvas.toDataURL('image/png', 1.0)
      
      // Proporción estándar de rollo de etiqueta térmica (100mm de ancho x alto proporcional)
      const labelW = 100
      const labelH = Math.round((canvas.height / canvas.width) * labelW)
      
      const pdf = new jsPDF({ 
        orientation: 'portrait', 
        unit: 'mm', 
        format: [labelW, labelH] 
      })
      
      pdf.addImage(imgData, 'PNG', 0, 0, labelW, labelH, undefined, 'FAST')

      const sku = safeFilePart(String(payload?.sku || 'PRODUCTO'))
      const code = safeFilePart(String(payload?.codigoBarras || 'CODIGO'))
      pdf.save(`LOGIXPORT_ETIQUETA_${sku}_${code}.pdf`)
      toast.success('Etiqueta PDF descargada correctamente')
    } catch {
      toast.error('Error al generar el PDF de la etiqueta')
    } finally {
      setGenerando(false)
    }
  }

  const handleCopiarDatos = () => {
    if (!payload) return
    const texto = `SKU: ${payload.sku || 'N/A'} | PRODUCTO: ${payload.nombre || 'N/A'} | UBICACIÓN: ${payload.ubicacion || 'N/A'} | LOTE: ${payload.lote || 'N/A'} | PESO: ${payload.peso || 0} kg | BARRAS: ${payload.codigoBarras || 'N/A'}`
    navigator.clipboard.writeText(texto)
    toast.success('Datos de manifiesto copiados al portapapeles')
  }

  useEffect(() => {
    if (!payload) return
    if (autoIntentado) return
    setAutoIntentado(true)
    const t = window.setTimeout(() => {
      handleDownloadPDF().catch(() => {})
    }, 700)
    return () => window.clearTimeout(t)
  }, [payload, autoIntentado])

  if (!payload) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0c0d12] flex items-center justify-center p-4">
        <Toaster position="top-right" />
        <div className="max-w-md w-full bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl p-8 shadow-xl text-center">
          <div className="p-3.5 rounded-2xl bg-amber-500/10 text-amber-600 dark:text-amber-400 w-fit mx-auto mb-4">
            <QrCodeIcon className="h-8 w-8" />
          </div>
          <h1 className="text-xl font-black text-slate-900 dark:text-white">Etiqueta no disponible</h1>
          <p className="mt-2 text-xs text-slate-500 dark:text-zinc-400">
            Este código QR no contiene datos válidos o ha expirado el parámetro de trazabilidad.
          </p>
          <Link
            to="/institucion/almacen-practica"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs shadow-md transition-all"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Volver al Almacén
          </Link>
        </div>
      </div>
    )
  }

  const largo = normalizarNumero(payload.largo)
  const ancho = normalizarNumero(payload.ancho)
  const alto = normalizarNumero(payload.alto)
  const dims = largo > 0 && ancho > 0 && alto > 0 ? `${largo.toFixed(2)} × ${ancho.toFixed(2)} × ${alto.toFixed(2)} m` : 'N/A'
  const cubicaje = (largo * ancho * alto) || normalizarNumero(payload.volumen)
  const volumenFormateado = normalizarNumero(payload.volumen).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 }) + ' m³'
  const cubicajeFormateado = normalizarNumero(cubicaje).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 }) + ' m³'
  const pesoFormateado = normalizarNumero(payload.peso).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 }) + ' kg'
  const ubicacionObj = parsearUbicacionLogistica(payload.ubicacion || '')

  return (
    <div className="min-h-screen bg-slate-100/80 dark:bg-[#0c0d12] text-slate-900 dark:text-zinc-100 pb-12 transition-colors">
      <Toaster position="top-right" />
      
      {/* Barra de Navegación Superior */}
      <header className="bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border-b border-slate-200/80 dark:border-white/10 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link 
              to="/institucion/almacen-practica" 
              className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-white/10 text-slate-600 dark:text-zinc-400 transition-colors"
              title="Volver al panel de almacén"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </Link>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono font-black tracking-widest text-amber-600 dark:text-amber-400 uppercase">
                  LogiXport WMS
                </span>
                <span className="text-slate-300 dark:text-zinc-600">/</span>
                <span className="text-[10px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-wider">
                  GS1-128
                </span>
              </div>
              <h1 className="text-sm sm:text-base font-extrabold text-slate-900 dark:text-white leading-tight">
                Etiqueta Térmica de Estiba y Pallet
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={handleCopiarDatos}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-700 dark:text-zinc-200 text-xs font-bold transition-all cursor-pointer border border-slate-200/60 dark:border-white/10"
              title="Copiar manifiesto"
            >
              <DocumentDuplicateIcon className="h-4 w-4" />
              <span>Copiar</span>
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-white/10 text-slate-600 dark:text-zinc-300 transition-colors cursor-pointer border border-slate-200/60 dark:border-white/10"
              title="Imprimir directamente"
            >
              <PrinterIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={handleDownloadPDF}
              disabled={generando}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-xs sm:text-sm text-white shadow-md shadow-amber-500/20 transition-all cursor-pointer ${
                generando ? 'bg-amber-400' : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700'
              }`}
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              <span>{generando ? 'Generando…' : 'Descargar PDF'}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Contenido Principal en 2 Columnas */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Columna Izquierda (7 cols): Canvas de la Etiqueta Térmica Física */}
          <div className="lg:col-span-7 flex flex-col space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-extrabold text-slate-800 dark:text-white uppercase tracking-wider">
                  Vista Previa de Etiqueta Térmica
                </h2>
                <p className="text-xs text-slate-500 dark:text-zinc-400">
                  Estándar industrial 100 × 150 mm (4×6") para impresoras Zebra / Honeywell
                </p>
              </div>
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold font-mono bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                ALTA RESOLUCIÓN 300 DPI
              </span>
            </div>

            {/* CONTENEDOR FÍSICO IMPRIMIBLE CAPTURADO POR HTML2CANVAS */}
            <div className="w-full flex justify-center bg-slate-200/70 dark:bg-black/40 p-4 sm:p-6 rounded-3xl border border-slate-300 dark:border-white/10 shadow-inner">
              <div
                ref={contentRef}
                data-thermal-label="true"
                className="bg-white text-black rounded-2xl border-2 border-black p-5 sm:p-6 select-none font-sans w-full"
                style={{ width: 440, maxWidth: '100%', boxSizing: 'border-box' }}
              >
                {/* 1. Header WMS & Trazabilidad */}
                <div className="flex items-center justify-between border-b-2 border-black pb-2 mb-3">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 rounded bg-black text-white text-[11px] font-black font-sans tracking-widest uppercase leading-tight inline-block">
                      LOGIXPORT WMS
                    </span>
                    <span className="text-[10px] font-black uppercase tracking-wider text-black leading-tight inline-block pt-0.5">
                      CEDIS CENTRAL · ESTIBA
                    </span>
                  </div>
                  <span className="text-[10px] font-sans tracking-wider font-bold text-black leading-tight pt-0.5">
                    {new Date().toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </span>
                </div>

                {/* 2. SKU y Descripción Principal */}
                <div className="space-y-1 mb-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-3xl font-black font-sans tracking-tight text-black leading-none pb-1">
                      {String(payload.sku || 'SKU-000').trim()}
                    </span>
                    <span className="px-2.5 py-1.5 rounded bg-black text-white text-[10px] font-black uppercase font-sans tracking-widest leading-tight inline-flex items-center justify-center">
                      {String(payload.estado || 'DISPONIBLE').toUpperCase()}
                    </span>
                  </div>
                  <p className="text-base font-black uppercase text-black leading-snug tracking-wide break-words m-0 pb-0.5">
                    {String(payload.nombre || 'Producto sin descripción')}
                  </p>
                  {(payload.marca || payload.modelo) && (
                    <p className="text-[11px] font-bold text-zinc-700 uppercase tracking-wide leading-normal m-0 pb-0.5">
                      MARCA / MODELO: {String(payload.marca || 'N/A')} {payload.modelo ? `· ${payload.modelo}` : ''}
                    </p>
                  )}
                </div>

                {/* 3. Coordenada Física en Rack (High-Contrast Reverse Black Box) */}
                <div className="bg-black text-white p-3.5 rounded-xl mb-3 font-sans">
                  <div className="flex items-center justify-between text-[9px] text-zinc-400 font-bold uppercase tracking-wider mb-1 leading-normal">
                    <span>COORDENADA MAESTRA DE ESTANTERÍA</span>
                    <span className="text-amber-400">WMS SLOTTING</span>
                  </div>
                  <div className="flex items-center justify-between text-sm sm:text-base font-black tracking-widest leading-normal">
                    <span className="text-amber-300">
                      ZONA {ubicacionObj.zona} · P{ubicacionObj.pasillo} · R{ubicacionObj.rack}
                    </span>
                    <span className="text-white pb-0.5">
                      N{ubicacionObj.nivel} · H{ubicacionObj.posicion}
                    </span>
                  </div>
                  <div className="mt-1.5 pt-1.5 border-t border-zinc-700/80 text-[10px] text-zinc-400 flex items-center justify-between leading-normal">
                    <span>UBICACIÓN: {String(payload.ubicacion || 'GEN-01-01-01-01')}</span>
                    <span>ALMACÉN: {ubicacionObj.almacen}</span>
                  </div>
                </div>

                {/* 4. Matriz de Atributos Físicos de Carga (4 Celdas Industriales) */}
                <div className="grid grid-cols-4 gap-1 py-2.5 border-y-2 border-black text-center text-black mb-3">
                  <div className="p-1">
                    <span className="text-[9px] uppercase font-bold text-zinc-600 block leading-tight">CANTIDAD</span>
                    <span className="text-xs font-black font-sans block whitespace-nowrap overflow-visible leading-normal pt-1 pb-0.5 tracking-wider">
                      {String(payload.cantidad || '1 UD')}
                    </span>
                  </div>
                  <div className="p-1 border-l border-zinc-300">
                    <span className="text-[9px] uppercase font-bold text-zinc-600 block leading-tight">PESO TOTAL</span>
                    <span className="text-xs font-black font-sans block whitespace-nowrap overflow-visible leading-normal pt-1 pb-0.5 tracking-wider">
                      {pesoFormateado}
                    </span>
                  </div>
                  <div className="p-1 border-l border-zinc-300">
                    <span className="text-[9px] uppercase font-bold text-zinc-600 block leading-tight">VOLUMEN</span>
                    <span className="text-xs font-black font-sans block whitespace-nowrap overflow-visible leading-normal pt-1 pb-0.5 tracking-wider">
                      {volumenFormateado}
                    </span>
                  </div>
                  <div className="p-1 border-l border-zinc-300">
                    <span className="text-[9px] uppercase font-bold text-zinc-600 block leading-tight">DIMENSIONES</span>
                    <span className="text-[10px] font-black font-sans block whitespace-nowrap overflow-visible leading-normal pt-1 pb-0.5 tracking-wider">
                      {dims}
                    </span>
                  </div>
                </div>

                {/* 5. Trazabilidad y Cadena de Suministro */}
                <div className="grid grid-cols-2 gap-2 text-xs font-sans mb-3 bg-zinc-100 p-2.5 rounded-lg border border-zinc-300">
                  <div>
                    <span className="font-bold text-zinc-500 block text-[9px] uppercase leading-tight">LOTE / BATCH:</span>
                    <span className="font-black text-black block overflow-visible leading-normal pt-1 pb-0.5 break-words">{String(payload.lote || 'STD-01')}</span>
                  </div>
                  <div>
                    <span className="font-bold text-zinc-500 block text-[9px] uppercase leading-tight">PROVEEDOR:</span>
                    <span className="font-black text-black block overflow-visible leading-normal pt-1 pb-0.5 break-words">{String(payload.proveedor || 'LOGIXPORT PARTNER')}</span>
                  </div>
                </div>

                {/* 6. Dual Code Engine: 2D QR Code + 1D GS1 Code 128 */}
                <div className="flex items-center justify-between gap-4 pt-1">
                  {/* QR 2D */}
                  <div className="flex flex-col items-center shrink-0">
                    <div className="p-1 rounded-lg border-2 border-black bg-white shadow-xs">
                      <img src={qrImg} alt="QR Code" className="w-32 h-32 sm:w-36 sm:h-36 object-contain" />
                    </div>
                    <span className="text-[9px] font-sans font-bold tracking-widest text-black uppercase mt-1 leading-normal">ESCÁNER 2D</span>
                  </div>

                  {/* Código de Barras 1D Code128 */}
                  <div className="flex-1 flex flex-col items-center justify-center min-w-0">
                    <span className="text-[9px] font-sans tracking-widest font-bold text-zinc-600 uppercase mb-2 leading-normal">
                      CÓDIGO DE BARRAS GS1-128
                    </span>
                    {barcode && barcodeDataUrl ? (
                      <div className="w-full bg-white py-2 px-1 rounded border border-zinc-200 flex items-center justify-center">
                        <img
                          src={barcodeDataUrl}
                          alt="Código de barras"
                          className="w-full h-auto max-h-16 object-contain"
                        />
                      </div>
                    ) : (
                      <div className="w-full bg-white py-2 px-1 rounded border border-zinc-200 flex items-center justify-center">
                        <img
                          src={barcode?.primary}
                          alt="Código de barras"
                          className="w-full h-auto max-h-16 object-contain"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* 7. Línea de corte térmico */}
                <div className="mt-3.5 pt-2 border-t-2 border-dashed border-zinc-300 flex items-center justify-between text-[9px] text-zinc-500 font-sans tracking-wider leading-normal pb-0.5">
                  <span>[NORMA GS1-128 / ETIQUETA TÉRMICA 100x150mm]</span>
                  <span>LOGIXPORT VERIFIED</span>
                </div>
              </div>
            </div>
          </div>

          {/* Columna Derecha (5 cols): Panel de Auditoría, Trazabilidad y Acciones */}
          <div className="lg:col-span-5 flex flex-col space-y-4">
            <div className="bg-white dark:bg-zinc-900 border border-slate-200/80 dark:border-white/10 rounded-3xl p-6 shadow-sm">
              <div className="flex items-center gap-2.5 pb-4 border-b border-slate-100 dark:border-white/5">
                <div className="p-2 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                  <ShieldCheckIcon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
                    Ficha Técnica y Manifiesto
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-zinc-400">
                    Datos sincronizados con la base de datos WMS
                  </p>
                </div>
              </div>

              {/* Lista Detallada de Atributos */}
              <div className="mt-4 space-y-2.5 text-xs">
                <div className="flex items-center justify-between py-1.5 border-b border-slate-100 dark:border-white/5">
                  <span className="text-slate-500 dark:text-zinc-400 font-medium flex items-center gap-1.5">
                    <CubeIcon className="w-3.5 h-3.5 text-blue-500" />
                    SKU Maestro
                  </span>
                  <span className="font-mono font-black text-slate-900 dark:text-white">{String(payload.sku || 'N/A').trim()}</span>
                </div>
                <div className="flex items-center justify-between py-1.5 border-b border-slate-100 dark:border-white/5">
                  <span className="text-slate-500 dark:text-zinc-400 font-medium flex items-center gap-1.5">
                    <CubeIcon className="w-3.5 h-3.5 text-slate-400" />
                    Descripción
                  </span>
                  <span className="font-bold text-slate-900 dark:text-white text-right max-w-[200px] truncate">{String(payload.nombre || 'N/A')}</span>
                </div>
                <div className="flex items-center justify-between py-1.5 border-b border-slate-100 dark:border-white/5">
                  <span className="text-slate-500 dark:text-zinc-400 font-medium">Marca / Modelo</span>
                  <span className="text-slate-700 dark:text-zinc-300 font-semibold text-right">{`${String(payload.marca || 'N/A')} · ${String(payload.modelo || 'N/A')}`}</span>
                </div>
                <div className="flex items-center justify-between py-1.5 border-b border-slate-100 dark:border-white/5">
                  <span className="text-slate-500 dark:text-zinc-400 font-medium flex items-center gap-1.5">
                    <MapPinIcon className="w-3.5 h-3.5 text-amber-500" />
                    Coordenada Rack
                  </span>
                  <span className="font-mono font-black text-amber-600 dark:text-amber-400">{ubicacionObj.formateado}</span>
                </div>
                <div className="flex items-center justify-between py-1.5 border-b border-slate-100 dark:border-white/5">
                  <span className="text-slate-500 dark:text-zinc-400 font-medium">Lote de Producción</span>
                  <span className="font-mono font-bold text-slate-800 dark:text-zinc-200">{String(payload.lote || 'STD-01')}</span>
                </div>
                <div className="flex items-center justify-between py-1.5 border-b border-slate-100 dark:border-white/5">
                  <span className="text-slate-500 dark:text-zinc-400 font-medium flex items-center gap-1.5">
                    <TruckIcon className="w-3.5 h-3.5 text-emerald-500" />
                    Proveedor
                  </span>
                  <span className="font-semibold text-slate-800 dark:text-zinc-200">{String(payload.proveedor || 'N/A')}</span>
                </div>
                <div className="flex items-center justify-between py-1.5 border-b border-slate-100 dark:border-white/5">
                  <span className="text-slate-500 dark:text-zinc-400 font-medium">Volumen / Cubicaje</span>
                  <span className="font-mono text-slate-700 dark:text-zinc-300 font-bold">{volumenFormateado} ({cubicajeFormateado})</span>
                </div>
                <div className="flex items-center justify-between py-1.5">
                  <span className="text-slate-500 dark:text-zinc-400 font-medium">Código GS1 / Barras</span>
                  <span className="font-mono text-slate-700 dark:text-zinc-300 font-bold break-all text-right">{String(payload.codigoBarras || payload.sku || 'N/A')}</span>
                </div>
              </div>

              {/* Pautas Operativas de Bodega */}
              <div className="mt-5 p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-xs space-y-1.5">
                <span className="font-bold text-amber-800 dark:text-amber-300 block uppercase text-[10px] tracking-wider">
                  ⚠️ Pauta de Operación para Montacarguista:
                </span>
                <p className="text-[11px] text-amber-900/90 dark:text-amber-200/90 leading-relaxed">
                  Adherir la etiqueta en la cara frontal derecha del pallet a 1.20 metros de altura para garantizar lectura automática con terminal de radiofrecuencia (RFID / Láser) al ingresar a la estantería.
                </p>
              </div>

              {/* Botones de Acción */}
              <div className="mt-6 space-y-2.5">
                <button
                  type="button"
                  onClick={handleDownloadPDF}
                  disabled={generando}
                  className={`w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-bold text-sm text-white shadow-lg shadow-amber-500/20 hover:shadow-amber-500/35 transition-all cursor-pointer ${
                    generando ? 'bg-amber-400' : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700'
                  }`}
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                  <span>{generando ? 'Generando PDF de Etiqueta…' : 'Descargar PDF de Etiqueta'}</span>
                </button>

                <div className="grid grid-cols-2 gap-2.5">
                  <button
                    type="button"
                    onClick={() => window.print()}
                    className="inline-flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl font-bold text-xs bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-800 dark:text-zinc-200 border border-slate-200/60 dark:border-white/10 transition-colors cursor-pointer"
                  >
                    <PrinterIcon className="h-4 w-4" />
                    <span>Imprimir Térmica</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleCopiarDatos}
                    className="inline-flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl font-bold text-xs bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-800 dark:text-zinc-200 border border-slate-200/60 dark:border-white/10 transition-colors cursor-pointer"
                  >
                    <DocumentDuplicateIcon className="h-4 w-4" />
                    <span>Copiar Manifiesto</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export const crearUrlEtiquetaProductoQR = (payload: PayloadEtiqueta) => {
  const url = new URL('/qr/almacen/etiqueta', window.location.origin)
  url.searchParams.set('d', encodePayload(payload))
  return url.toString()
}

export default PaginaEtiquetaProductoQR

